import { Duration, isYesterday } from "date-fns";
import format from "date-fns/format";
import formatDuration from "date-fns/formatDuration";
import isThisYear from "date-fns/isThisYear";
import isToday from "date-fns/isToday";
import isTomorrow from "date-fns/isTomorrow";
import { useCallback } from "react";
import { useLanguage } from "../i18n/EnhancedIntlProvider";
import { useTranslations } from "./useTranslations";

export type DateLabelFormattingVariant =
  | "regular"
  | "concise"
  | "concise-full"
  | "full";

export type DateTimeLabelFormattingVariant = "regular" | "concise";

interface HumanizesPastDateOptions {
  customJustNowMessage?: string;
}

interface DurationFormatOptions {
  abbreviated?: boolean;
}

export const useFormattedDate = () => {
  const { t } = useTranslations();
  const { dateLocale } = useLanguage();

  const localeAwareFormat = useCallback(
    (date: Date | number, formatString: string) =>
      format(date, formatString, {
        locale: dateLocale,
        weekStartsOn: 1,
      }),
    [dateLocale]
  );

  const localeAwareFormatDuration = useCallback(
    (duration: Duration, options?: DurationFormatOptions) => {
      if (options?.abbreviated) {
        const parts: string[] = [];

        if (duration.years) {
          parts.push(`${duration.years}y`);
        }
        if (duration.months) {
          parts.push(`${duration.months}mo`);
        }
        if (duration.weeks) {
          parts.push(`${duration.weeks}w`);
        }
        if (duration.days) {
          parts.push(`${duration.days}d`);
        }
        if (duration.hours) {
          parts.push(`${duration.hours}h`);
        }
        if (duration.minutes) {
          parts.push(`${duration.minutes}m`);
        }
        if (duration.seconds) {
          parts.push(`${duration.seconds}s`);
        }
        if (parts.length === 0) {
          return "0s";
        }

        return parts.join(" ");
      }

      return formatDuration(duration, { locale: dateLocale });
    },
    [dateLocale]
  );

  /**
   * Formats a date label based on the given variant:
   * - `regular` => `December 25, 2023`
   * - `concise` => `Dec 25, 2023`
   */
  const formatDateLabel = useCallback(
    (date: Date | number, variant: DateLabelFormattingVariant = "regular") => {
      if (isToday(date)) {
        return t({ id: "common.today", defaultMessage: "Today" });
      }

      if (isTomorrow(date)) {
        return t({ id: "common.tomorrow", defaultMessage: "Tomorrow" });
      }

      if (isYesterday(date)) {
        return t({ id: "common.yesterday", defaultMessage: "Yesterday" });
      }

      const formattedYear = isThisYear(date) ? "" : ", uuuu";

      if (variant === "concise") {
        return localeAwareFormat(date, "MMM d".concat(formattedYear));
      }

      if (variant === "concise-full") {
        return localeAwareFormat(date, "MMM d, uuuu");
      }

      if (variant === "full") {
        return localeAwareFormat(date, "MMMM d, uuuu");
      }

      return localeAwareFormat(date, "MMMM d".concat(formattedYear));
    },
    [localeAwareFormat, t]
  );

  /**
   * Formats a date time label based on the given variant:
   * - `regular` => `19 Jul 2024, 12:00 PM`
   */
  const formatDateTimeLabel = useCallback(
    (
      date: Date | number,
      variant: DateTimeLabelFormattingVariant = "regular"
    ) => {
      const dateLabel = (() => {
        if (isToday(date)) {
          return t({ id: "common.today", defaultMessage: "Today" });
        }

        if (isTomorrow(date)) {
          return t({ id: "common.tomorrow", defaultMessage: "Tomorrow" });
        }

        if (variant === "concise") {
          return localeAwareFormat(
            date,
            isThisYear(date) ? "MMM d" : "MMM d, uuuu"
          );
        }

        return localeAwareFormat(
          date,
          isThisYear(date) ? "d MMMM" : "d MMM uuuu"
        );
      })();

      const timeLabel = localeAwareFormat(date, "hh:mm a");

      return `${dateLabel}, ${timeLabel}`;
    },
    [localeAwareFormat, t]
  );

  /**
   * Formats a time label (time only, no date):
   * - `12:00 PM`
   */
  const formatTimeLabel = useCallback(
    (date: Date | number) => {
      return localeAwareFormat(date, "hh:mm a");
    },
    [localeAwareFormat]
  );

  /**
   * Humanizes a past date based on the given date.
   * (e.g. just now, 5 minutes ago, 1 hour ago, etc.)
   */
  const humanizePastDate = useCallback(
    (date: Date, options?: HumanizesPastDateOptions) => {
      const diff = Date.now() - date.getTime();

      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));

      if (seconds < 60) {
        return (
          options?.customJustNowMessage ||
          t({
            id: "common.just-now",
            defaultMessage: "Just now",
          })
        );
      }

      if (minutes < 60) {
        return t(
          {
            id: "common.date-minutes-ago",
            defaultMessage:
              "{minutes} {minutes, plural, one {minute} other {minutes}} ago",
          },
          {
            minutes,
          }
        );
      }

      if (hours < 24) {
        return t(
          {
            id: "common.date-hours-ago",
            defaultMessage:
              "{hours} {hours, plural, one {hour} other {hours}} ago",
          },
          {
            hours,
          }
        );
      }

      return localeAwareFormat(date, "dd MMM yyyy, hh:mm a");
    },
    [localeAwareFormat, t]
  );

  return {
    localeAwareFormat,
    localeAwareFormatDuration,
    formatDateLabel,
    formatDateTimeLabel,
    formatTimeLabel,
    humanizePastDate,
  };
};
